import React, {
    useCallback, useEffect, useMemo, useRef, useState
} from "react";
import styled, { css } from "styled-components";

import IcoAccessTime from "@floji-internal/mui-icons/AccessTime";
import IcoBubbleChart from "@floji-internal/mui-icons/BubbleChart";
import IcoDriveEta from "@floji-internal/mui-icons/DriveEta";
import IcoEmojiEmotions from "@floji-internal/mui-icons/EmojiEmotions";
import IcoEmojiObjects from "@floji-internal/mui-icons/EmojiObjects";
import IcoEmojiPeople from "@floji-internal/mui-icons/EmojiPeople";
import IcoEmojiSymbols from "@floji-internal/mui-icons/EmojiSymbols";
import IcoFlag from "@floji-internal/mui-icons/Flag";
import IcoPets from "@floji-internal/mui-icons/Pets";
import IcoRestaurantMenu from "@floji-internal/mui-icons/RestaurantMenu";
import IcoSportsCricket from "@floji-internal/mui-icons/SportsCricket";
import IcoStar from "@floji-internal/mui-icons/Star";

import { skinTones, tEmoji, tEmojiCategory, tPickerEmoji, tSkinTone, categories as flomojiCategories } from "@floji-internal/noto-emoji";

import { Strings } from "--root/strings/en";

import { EmojiSprite } from "--components/emoji";
import { TextInput } from "--floponents-web/atoms/TextInput";
import { Body2, Heading3 } from "--components/typography";
import { scrollbarCSS } from "--floponents-web/common/scrollbarCss";

import { SwaggerMutateClient, } from "--client/common/apis/rest";

import { EmojiWrapper } from "./components/EmojiButton";
import EmojiDetails from "./components/EmojiDetails";
import TonePicker from "./components/TonePicker";
import EmojiPickerCategory from "./components/EmojiCategory";
import useEmojiPickerUserMetadata from "--floponents-web/organisms/EmojiPicker/useEmojiPickerUserMetadata";
import { useEmojiList } from "../../../hooks/useEmojiList";

/** Emoji to display as tone picker button. */
const EMOJI_SHORTCUT_TONE_PICKER = ":vulcan_salute:";
/** Emoji to display in search results if none are found. */
const EMOJI_SHORTCUT_NO_RESULTS = ":t_rex:";

/** Categories derived from user-specific data.  */
enum UserEmojiCategory {
    Recent = "recent",
    Starred = "starred"
}

/** Extended emoji category name type. */
type EmojiCategory = tEmojiCategory | UserEmojiCategory;

/** Type guard for user emoji categories. */
function isUserEmojiCategory(
    category: EmojiCategory
): category is UserEmojiCategory {
    return (
        category === UserEmojiCategory.Recent ||
        category === UserEmojiCategory.Starred
    );
}

/** Map of emoji categories -> icon components. */
const categoryIconMap = {
    [UserEmojiCategory.Recent]: IcoAccessTime,
    [UserEmojiCategory.Starred]: IcoStar,
    smileys: IcoEmojiEmotions,
    people: IcoEmojiPeople,
    nature: IcoPets,
    food: IcoRestaurantMenu,
    travel: IcoDriveEta,
    activities: IcoSportsCricket,
    objects: IcoEmojiObjects,
    symbols: IcoEmojiSymbols,
    flags: IcoFlag,
    custom: IcoBubbleChart
} as const;

interface StyledEmojiPickerProps {
    width: number;
    height: number;
}
const StyledEmojiPicker = styled.div.attrs<StyledEmojiPickerProps>(props => ({
    style: {
        width: `${props.width}px`,
        height: `${props.height}px`
    }
})) <StyledEmojiPickerProps>`
    background: ${props => props.theme.palette.surface};
    display: flex;
    flex-direction: column;
    height: ${props => props.height}px;
    overflow: hidden;
    position: relative;
    user-select: none;
    width: ${props => props.width}px;
`;

const EmojiPickerTabs = styled.div`
    background: ${props => props.theme.palette.surface};
    border-bottom: 1px solid ${props => props.theme.palette.ui.border};
    display: flex;
    flex-shrink: 0;
    justify-content: space-between;
    padding: ${props => props.theme.spacing(0, 2)};
    position: sticky;
    top: 0;
`;
const EmojiPickerTabSeparator = styled.div`
    align-self: center;
    background-color: ${props => props.theme.palette.ui.border};
    height: 60%;
    margin: 0 ${props => props.theme.spacing(0.5)};
    min-width: 1px;
    width: 1px;
`;
interface EmojiPickerTabProps {
    active?: boolean;
}
const EmojiPickerTab = styled.div<EmojiPickerTabProps>`
    color: ${props => props.theme.palette.text.secondary};
    cursor: pointer;
    float: left;
    padding: ${props => props.theme.spacing(1, 0.25)};
    position: relative; 

    /** Tab line */
    ::after {
        content: "";
        bottom: 0;
        height: 2px;
        left: 0;
        position: absolute;
        right: 0;
    }

    > svg {
        display: block;
    }

    ${props => props.active && css`
        color: ${props.theme.palette.primary.main};
        ::after {
            background-color: ${props.theme.palette.primary.main};
        }
    `}
`;

const EmojiPickerSearch = styled.div`
    padding: ${props => props.theme.spacing(2)};
`;
const EmojiPickerSearchResults = styled.div`
    flex: 1 1 auto;
    margin: 0 ${props => props.theme.spacing(2)};
    position: relative;
    ${scrollbarCSS()};
`;
const EmojiPickerNoResults = styled.div`
    text-align: center;
`;
const EmojiPickerNoResultsButton = styled(EmojiWrapper)`
    margin: ${props => props.theme.spacing(1)};
`;
const EmojiPickerNoResultsTitle = styled(Body2)``;

const EmojiPickerCategories = styled.div`
    flex: 1 1 auto;
    margin: 0 ${props => props.theme.spacing(2)};
    position: relative;
    ${scrollbarCSS()};
`;
const EmojiPickerFooter = styled.div`
    align-items: center;
    border-top: 1px solid ${props => props.theme.palette.ui.border};
    box-sizing: border-box;
    display: flex;
    min-height: 64px;
    padding: ${props => props.theme.spacing(1, 2)};
    position: relative;
`;

const EmojiPickerFooterHeading = styled(Heading3).attrs({ as: "span" })`
    color: ${props => props.theme.palette.text.secondary};
    margin-right: auto;
`;


export const filterEmoji = (emojiList: tPickerEmoji[] | undefined) => (term: string) => {
    let emojis: Array<{ emoji: tPickerEmoji, score: number }> = [];
    if (!emojiList) return [];
    for (let emoji of emojiList) {
        if (emoji.shortcut.indexOf(term) >= 0) {
            const emojiNormalised = emoji.shortcut.substr(1, emoji.shortcut.length - 2)
            const emojiPadded = "_" + emojiNormalised + "_"
            const score = (emojiNormalised.startsWith(term) ? 1 : 0) + (emojiNormalised.endsWith(term) ? 1 : 0) + ((emojiPadded).includes("_" + term + "_") ? 1 : 0)
                + term.length / emoji.shortcut.length + (term.length === 0 && emoji.category === "smileys" && 1 || 0)
            emojis.push({ emoji, score });

        }
    }
    emojis.sort((a, b) => b.score - a.score)
    return emojis.slice(0, 50).map(v => v.emoji);
}

export interface EmojiPickerProps {
    /** Width of emoji picker panel (defaults to 512). */
    width?: number;
    /** Height of emoji picker panel (defaults to 400). */
    height?: number;
    /** Called when an emoji is picked. */
    onEmojiPicked?: (emoji: tEmoji) => void;
}
const EmojiPicker = (props: EmojiPickerProps) => {
    const list = useEmojiList();
    if (list) return <_EmojiPicker {...props} list={list} />
    return null;
}
const _EmojiPicker = (props: EmojiPickerProps & { list: NonNullable<ReturnType<typeof useEmojiList>> }) => {
    const {
        width = 512,
        height = 400,
        list,
        onEmojiPicked,
    } = props;

    const { metadata, refreshMetadata } = useEmojiPickerUserMetadata();

    /** Gets flomoji details from emoji character. */
    const getEmojiByChar = useCallback((char: string) => {
        const matchingEmoji = Object.values(list.emojisByShortcut)
            .find(emoji => emoji.emoji === char);
        if (!matchingEmoji) {
            throw new Error("Invalid emoji character or not in database!");
        }
        return matchingEmoji;
    }, [list]);

    // Current search term (if searching)
    const [searchTerm, setSearchTerm] = useState("");
    const handleSearchInput = useCallback((
        _ev: React.ChangeEvent<HTMLInputElement>,
        value: string
    ) => {
        setSearchTerm(value);
    }, []);

    /** Results from `emojiFilter()` with current search term. */
    const searchResults = useMemo(
        () => (searchTerm ? filterEmoji(list.emojis)(searchTerm) : null),
        [list, searchTerm]
    );

    /** Emoji categories with elements scrolled into view. */
    const [visibleCategories, setVisibleCategories] =
        useState(new Set<EmojiCategory>(new Set([])));

    /** Emoji categories with > 1 emoji. */
    const categories = useMemo<EmojiCategory[]>(() => {
        const filteredFlomojiCategories: EmojiCategory[] =
            Array.from(flomojiCategories.keys())
                .filter(key => list.emojisByCategory[key]?.length);

        // Add user-specific emoji metadata categories (reverse order)
        if (metadata?.recents?.length) {
            filteredFlomojiCategories.unshift(UserEmojiCategory.Recent);
        }
        if (metadata?.starred?.length) {
            filteredFlomojiCategories.unshift(UserEmojiCategory.Starred);
        }

        /**
         * If previously visible category no longer exists, remove it since
         * there's no opportunity to un-intersect naturally.
         */
        setVisibleCategories(existingCategories => new Set(
            [...existingCategories.values()].filter(
                category => filteredFlomojiCategories.includes(category)
            )
        ));

        return filteredFlomojiCategories;
    }, [
        list,
        metadata?.recents?.length,
        metadata?.starred?.length
    ]);

    /** Emoji category elements. */
    const categoryElementMap = useRef<Map<EmojiCategory, HTMLDivElement>>(new Map());

    /** Last visible category (for tab indicator). */
    const activeCategory = useMemo(() => (
        Array.from(visibleCategories.values())
            .sort((a, b) => categories.indexOf(a) - categories.indexOf(b))
            .at(0)
    ), [visibleCategories, categories]);

    /**
     * Handle scrolling to emoji category associated with tab.
     * @param category Category associated with tab.
     */
    const onTabClicked = useCallback((category: EmojiCategory) => {
        const categoryElement = categoryElementMap.current.get(category);
        if (!categoryElement) return;

        /**
         * Bit of a hack to get the intersection observer for the previous
         * category to fire removing it from visible category state.
         */
        const top = categoryElement.offsetTop + 1;

        // Stop searching
        if (searchTerm) {
            setSearchTerm("");
            // Wait until next paint to scroll
            window.requestAnimationFrame(() => {
                categoriesElement.current?.scroll({ top });
            });
        } else {
            categoriesElement.current?.scroll({ top, behavior: "smooth" });
        }
    }, [searchTerm]);

    /** Category list element. */
    const categoriesElement = useRef<HTMLDivElement>(null);

    /**
     * Tabs in the emoji picker are just indicators for which emoji category is
     * currently scrolled into view, rather than a stack of views for each
     * category.
     * 
     * Intersection observers are registered for the category elements and the
     * list of visible categories is updated each time they intersect with the
     * viewport. The first visible category according to the original order
     * becomes the active tab.
     */
    useEffect(() => {
        if (!categoriesElement.current) return;

        /** Intersection observers registered on category elements. */
        const registeredObservers: IntersectionObserver[] = [];

        // Register observers for category elements
        for (const category of categories) {
            const categoryElement = categoryElementMap.current.get(category);
            if (!categoryElement) continue;

            const observer = new IntersectionObserver(entries => {
                setVisibleCategories(existingCategories => {
                    let newVisibleCategories = existingCategories;

                    for (const entry of entries) {
                        if (entry.isIntersecting) {
                            if (!newVisibleCategories.has(category)) {
                                if (newVisibleCategories === existingCategories) {
                                    newVisibleCategories = new Set(existingCategories);
                                }
                                newVisibleCategories.add(category);
                            }
                        } else if (newVisibleCategories.has(category)) {
                            if (newVisibleCategories === existingCategories) {
                                newVisibleCategories = new Set(existingCategories);
                            }
                            newVisibleCategories.delete(category);
                        }
                    }

                    return newVisibleCategories;
                });
            }, {
                root: categoriesElement.current
            });

            observer.observe(categoryElement);
            registeredObservers.push(observer);
        }

        return () => {
            for (const observer of registeredObservers) observer.disconnect();
        };
    }, [categories]);

    /** Currently selected skin tone. */
    const [activeTone, setActiveTone] = useState<tSkinTone>();

    /** Whether tone picker UI is open. */
    const [isTonePickerOpen, setIsTonePickerOpen] = useState(false);
    const handleTonePickerToggled = useCallback((isOpen: boolean) => {
        setIsTonePickerOpen(isOpen);
    }, []);

    // Hovered emoji used for footer details
    const [highlightedEmoji, setHighlightedEmoji] = useState<tPickerEmoji>();
    const handleEmojiHighlighted = useCallback((emoji: tPickerEmoji) => {
        setHighlightedEmoji(emoji);
    }, []);

    /** Handles picks from categories and passes picked emoji to consumer. */
    const handleEmojiPicked = useCallback((pickedEmoji: tPickerEmoji) => {
        let { emoji, shortcut, name } = pickedEmoji;

        SwaggerMutateClient.RememberEmojiPickerRecentEmoji({ emoji })
            .then(refreshMetadata);

        const pickedTone = activeTone && pickedEmoji.tones?.[activeTone];
        if (pickedTone) {
            emoji = pickedTone.emoji;
            shortcut = `${shortcut}${skinTones[activeTone].shortcut}`;
            name = `${name} - ${pickedTone[activeTone].name.en}`;
        }

        onEmojiPicked?.({ emoji, shortcut, name });
    }, [activeTone, onEmojiPicked, refreshMetadata]);

    const handleEmojiStarred = useCallback((emoji: tPickerEmoji) => {
        SwaggerMutateClient.StarEmojiPickerEmoji({ emoji: emoji.emoji })
            .then(refreshMetadata);
    }, [refreshMetadata]);

    const handleEmojiUnstarred = useCallback((emoji: tPickerEmoji) => {
        SwaggerMutateClient.UnstarEmojiPickerEmoji({ emoji: emoji.emoji })
            .then(refreshMetadata);
    }, [refreshMetadata]);

    /** Handles updating active tone. */
    const handleTonePicked = useCallback((tone?: tSkinTone) => {
        setActiveTone(tone);
    }, []);

    /** Handle no search results T-Rex emoji click. */
    const handleTyrannosaurClicked = useCallback(() => {
        handleEmojiPicked(list.emojisByShortcut[EMOJI_SHORTCUT_NO_RESULTS]!);
    }, [list, handleEmojiPicked]);

    const recentEmoji = useMemo(() => (
        metadata?.recents?.map(getEmojiByChar)
    ), [getEmojiByChar, metadata?.recents]);
    const starredEmoji = useMemo(() => (
        metadata?.starred?.map(getEmojiByChar)
    ), [getEmojiByChar, metadata?.starred]);

    /** Memoised category list to fix performance issues with re-rendering. */
    const renderedCategories = useMemo(() => (
        categories.map(category => {
            let categoryEmoji: tPickerEmoji[] = list.emojisByCategory[category];
            let customTitle: string | undefined;

            const isUserCategory = isUserEmojiCategory(category);
            if (isUserCategory) {
                switch (category) {
                    case UserEmojiCategory.Recent:
                        if (recentEmoji?.length) categoryEmoji = recentEmoji;
                        customTitle = Strings.emojiPicker.recentlyUsed;
                        break;
                    case UserEmojiCategory.Starred:
                        if (starredEmoji?.length) categoryEmoji = starredEmoji;
                        customTitle = Strings.emojiPicker.starred;
                        break;

                    default:
                        break;
                }
            }

            const handleCleared = async () => {
                if (!isUserCategory) return;
                await ({
                    [UserEmojiCategory.Recent]: SwaggerMutateClient.ClearEmojiPickerRecentEmoji,
                    [UserEmojiCategory.Starred]: SwaggerMutateClient.ClearEmojiPickerStarredEmoji
                })[category]({});

                refreshMetadata();
            };

            return (
                <EmojiPickerCategory
                    key={category}
                    ref={ref => ref && categoryElementMap.current.set(category, ref)}
                    category={!isUserCategory ? category : undefined}
                    isClearable={isUserCategory}
                    customTitle={customTitle}
                    emoji={categoryEmoji}
                    tone={activeTone}
                    starredEmoji={starredEmoji}
                    onCleared={handleCleared}
                    onEmojiHighlighted={handleEmojiHighlighted}
                    onEmojiPicked={handleEmojiPicked}
                    onEmojiStarred={handleEmojiStarred}
                    onEmojiUnstarred={handleEmojiUnstarred}
                />
            );
        })
    ), [
        categories, list, activeTone, starredEmoji,
        handleEmojiHighlighted, handleEmojiPicked, handleEmojiStarred,
        handleEmojiUnstarred, recentEmoji, refreshMetadata
    ]);
    return (
        <StyledEmojiPicker width={width} height={height} onWheel={e => e.stopPropagation()}>
            <EmojiPickerTabs>
                {categories.map((category, index) => {
                    const CategoryIcon = categoryIconMap[category];
                    const tab = (
                        <EmojiPickerTab
                            key={category}
                            active={activeCategory === category}
                            onClick={() => onTabClicked(category)}
                        >
                            <CategoryIcon size="20px" />
                        </EmojiPickerTab>
                    );

                    // Find boundary between user/default categories
                    if (
                        isUserEmojiCategory(category) &&
                        !isUserEmojiCategory(categories?.[index + 1]!)
                    ) {
                        return <>
                            {tab}
                            <EmojiPickerTabSeparator />
                        </>;
                    }

                    return tab;
                })}
            </EmojiPickerTabs>

            <EmojiPickerSearch>
                <TextInput
                    type="search"
                    placeholder={Strings.emojiPicker.search}
                    value={searchTerm}
                    onChange={handleSearchInput}
                />
            </EmojiPickerSearch>

            {searchResults && <EmojiPickerSearchResults>
                {searchResults.length
                    ? <EmojiPickerCategory
                        customTitle={Strings.emojiPicker.searchResults}
                        emoji={searchResults}
                        tone={activeTone}
                        starredEmoji={starredEmoji}
                        onEmojiHighlighted={handleEmojiHighlighted}
                        onEmojiPicked={handleEmojiPicked}
                        onEmojiStarred={handleEmojiStarred}
                        onEmojiUnstarred={handleEmojiUnstarred}
                    />
                    : <EmojiPickerNoResults>
                        <EmojiPickerNoResultsButton onClick={handleTyrannosaurClicked}>
                            <EmojiSprite
                                emoji={list.emojisByShortcut[EMOJI_SHORTCUT_NO_RESULTS]!}
                                size={64}
                                spriteSize={64}
                            />
                        </EmojiPickerNoResultsButton>
                        <EmojiPickerNoResultsTitle>
                            {Strings.emojiPicker.noResults}
                        </EmojiPickerNoResultsTitle>
                    </EmojiPickerNoResults>}
            </EmojiPickerSearchResults>}

            <EmojiPickerCategories ref={categoriesElement} hidden={!!searchTerm}>
                {renderedCategories}
            </EmojiPickerCategories>

            <EmojiPickerFooter>
                {highlightedEmoji && !isTonePickerOpen
                    /**
                     * The emoji info covers the tone picker, so only
                     * show when the tone picker is closed.
                     */
                    ? <EmojiDetails emoji={highlightedEmoji} tone={activeTone} />
                    : <>
                        <EmojiPickerFooterHeading>
                            {Strings.emojiPicker.chooseAnEmoji}
                        </EmojiPickerFooterHeading>

                        <TonePicker
                            emoji={list.emojisByShortcut[EMOJI_SHORTCUT_TONE_PICKER]!}
                            activeTone={activeTone}
                            isOpen={isTonePickerOpen}
                            onToggled={handleTonePickerToggled}
                            onTonePicked={handleTonePicked}
                        />
                    </>}
            </EmojiPickerFooter>
        </StyledEmojiPicker>
    );
};

export default EmojiPicker;
